/**
 * Month Calendar Picker Component
 * Task 002b: Display month-level calendar for month navigation
 * Replaces the month dropdown with visual calendar interface
 */

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface MonthCalendarPickerProps {
  availableMonths: string[]; // Array of YYYY-MM-DD format dates
  currentMonth: string; // YYYY-MM-DD format
  onSelectMonth: (month: string) => void;
  onClose: () => void;
}

export const MonthCalendarPicker: React.FC<MonthCalendarPickerProps> = ({
  availableMonths,
  currentMonth,
  onSelectMonth,
  onClose,
}) => {
  // Parse current month to get initial year
  // Ensure we have a valid date, fallback to current year if parsing fails
  console.log('[MonthCalendarPicker] Received currentMonth:', currentMonth);

  const currentDate = new Date(currentMonth);
  const isValidDate = !isNaN(currentDate.getTime());

  console.log('[MonthCalendarPicker] Parsed date:', currentDate, 'isValid:', isValidDate);

  // Get initial year - use the parsed date if valid, otherwise extract from first available month
  let initialYear = new Date().getFullYear();
  if (isValidDate) {
    initialYear = currentDate.getFullYear();
  } else if (availableMonths.length > 0) {
    // Try to extract year from first available month
    const firstMonth = new Date(availableMonths[0]);
    if (!isNaN(firstMonth.getTime())) {
      initialYear = firstMonth.getFullYear();
    }
  }

  console.log('[MonthCalendarPicker] Initial year:', initialYear);

  const [viewYear, setViewYear] = useState(initialYear);
  const { t } = useTranslation();

  const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  const monthNames = monthKeys.map(key => t(`budget.months_short.${key}`));

  const handlePrevYear = () => {
    setViewYear(prev => prev - 1);
  };

  const handleNextYear = () => {
    setViewYear(prev => prev + 1);
  };

  const handleMonthClick = (monthIndex: number) => {
    // Format as YYYY-MM-DD (first day of month)
    const selectedMonth = `${viewYear}-${String(monthIndex + 1).padStart(2, '0')}-01`;
    
    // Only allow selection if month exists in availableMonths
    if (availableMonths.includes(selectedMonth)) {
      onSelectMonth(selectedMonth);
      onClose();
    }
  };

  const isMonthAvailable = (monthIndex: number): boolean => {
    const monthDate = `${viewYear}-${String(monthIndex + 1).padStart(2, '0')}-01`;
    return availableMonths.includes(monthDate);
  };

  const isCurrentMonth = (monthIndex: number): boolean => {
    // Only highlight if currentMonth is valid
    if (!isValidDate) return false;

    const monthDate = `${viewYear}-${String(monthIndex + 1).padStart(2, '0')}-01`;
    return monthDate === currentMonth;
  };

  const isTodayMonth = (monthIndex: number): boolean => {
    const today = new Date();
    return viewYear === today.getFullYear() && monthIndex === today.getMonth();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Calendar Container - Pixel Brutalism Style */}
        <div
          className="bg-white border-2 border-[#077f5d] w-full max-w-md p-6"
          style={{
            boxShadow: '8px 8px 0 #077f5d',
            transition: 'none'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with Year Navigation */}
          <div className="flex items-center justify-between mb-6 bg-[#a9edba] border-2 border-[#077f5d] p-3">
            <button
              onClick={handlePrevYear}
              className="p-1 hover:bg-white border-2 border-transparent hover:border-[#077f5d]"
              style={{ transition: 'none' }}
              aria-label="Previous year"
            >
              <ChevronLeft className="w-5 h-5 text-[#077f5d]" />
            </button>

            <h2
              className="font-bold text-black uppercase"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                fontSize: '14px',
                lineHeight: '1.4'
              }}
            >
              {viewYear}
            </h2>

            <button
              onClick={handleNextYear}
              className="p-1 hover:bg-white border-2 border-transparent hover:border-[#077f5d]"
              style={{ transition: 'none' }}
              aria-label="Next year"
            >
              <ChevronRight className="w-5 h-5 text-[#077f5d]" />
            </button>
          </div>

          {/* Month Grid */}
          <div className="grid grid-cols-3 gap-3">
            {monthNames.map((monthName, index) => {
              const available = isMonthAvailable(index);
              const selected = isCurrentMonth(index);
              const today = isTodayMonth(index);

              return (
                <button
                  key={index}
                  onClick={() => handleMonthClick(index)}
                  disabled={!available}
                  className={`
                    px-4 py-3 border-2 font-bold uppercase
                    ${selected
                      ? 'bg-[#077f5d] text-white border-[#077f5d]'
                      : available
                        ? 'bg-white text-[#077f5d] border-[#077f5d] hover:bg-[#dffbd0]'
                        : 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                    }
                    ${today && !selected ? 'border-[#3cbe8c] border-4' : ''}
                  `}
                  style={{
                    boxShadow: available && !selected ? '3px 3px 0 #077f5d' : 'none',
                    transition: 'none',
                    fontFamily: "'Press Start 2P', monospace",
                    fontSize: '10px'
                  }}
                >
                  {monthName}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-6 pt-4 border-t-2 border-[#077f5d]">
            <div className="flex flex-wrap gap-4 text-xs font-mono text-[#1a1a1a]">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-[#077f5d] border-2 border-[#077f5d]"></div>
                <span>{t('month_picker.selected_month')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-white border-4 border-[#3cbe8c]"></div>
                <span>{t('month_picker.current_month')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-white border-2 border-[#077f5d]"></div>
                <span>{t('month_picker.available')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
